import { apiClient as api } from "./httpClient";
import type { CashBookSlice } from "./accountsPayableService";

// Relatórios analíticos — fonte: espelho do Bubble (leitura) via API.

export interface ScheduleAnalysisTotals {
  totalSchedules: number;
  attended: number;
  chargeableNoShows: number;
  cancelled: number;
  attendanceRate: number;
  realizedValue: number;
  paidValue: number;
  pendingValue: number;
  advancePaidValue: number;
  professionalPayout: number;
  averageTicket: number;
  /** atendimentos cobráveis ainda não pagos (os que compõem pendingValue) */
  pendingCount?: number;
}

export interface StatusCountItem {
  status: string;
  /** presença com significado "atendido" (RN-114d) */
  attended?: boolean;
  count: number;
  color?: string | null;
  grossValue: number;
  payoutValue: number;
}

export interface ProfessionalAnalysisItem {
  professionalExternalId: string;
  professionalName: string;
  totalSchedules: number;
  attended: number;
  cancelledByPatient: number;
  cancelledByProfessional: number;
  notAttended: number;
  realizedValue: number;
  professionalPayout: number;
}

export interface MonthlyAnalysisItem {
  month: string;
  totalSchedules: number;
  attended: number;
  realizedValue: number;
  professionalPayout: number;
}

export interface ScheduleAnalysisResponse {
  totals: ScheduleAnalysisTotals;
  byStatus: StatusCountItem[];
  byPresence: StatusCountItem[];
  byProfessional: ProfessionalAnalysisItem[];
  monthly: MonthlyAnalysisItem[];
  /** sessões cobráveis pagas por forma de pagamento (recibo com duas formas rateia) */
  byPaymentType: PaymentTypeShareItem[];
  /** atendimentos realizados por origem do serviço */
  byOrigin: OriginShareItem[];
  /** uma linha por plano (Particular, AMIL 50 ao 380, ...) */
  byPlan: PlanAnalysisItem[];
}

export interface PaymentTypeShareItem {
  name: string;
  count: number;
  value: number;
}

export interface OriginShareItem {
  origin: string;
  count: number;
  value: number;
}

export interface PlanAnalysisItem {
  plan: string;
  origin: string;
  attended: number;
  realizedValue: number;
  paidValue: number;
  pendingValue: number;
}

export interface ReportProfessionalItem {
  externalId: string;
  name: string;
  /** false = vínculo inativo com agendamento no histórico (aparece sob "Mostrar inativos") */
  active?: boolean;
}

export interface ScheduleAnalysisParams {
  from: string; // YYYY-MM-DD
  to: string;   // YYYY-MM-DD
  professionalIds?: string[];
}

/**
 * Produtividade e repasse por profissional (RN-215): a agenda cruzada com as evoluções.
 * A linha do profissional é o SUBTOTAL — a soma exata das linhas de serviço dentro dela.
 */
export interface ProductivityServiceRow {
  serviceName: string;
  totalSchedules: number;
  cancelledByPatient: number;
  cancelledByProfessional: number;
  attended: number;
  evolved: number;
  payoutAttended: number;
  payoutEvolved: number;
  /** Evoluídos ÷ comparecidos. A tela NÃO exibe percentual nas linhas de subtotal e total. */
  evolvedRate: number;
}

export interface ProfessionalProductivityItem extends Omit<ProductivityServiceRow, "serviceName"> {
  professionalExternalId: string;
  professionalName: string;
  services: ProductivityServiceRow[];
}

export interface ProfessionalProductivityMonth {
  month: string;
  attended: number;
  evolved: number;
  payoutAttended: number;
  payoutEvolved: number;
}

export interface ProfessionalProductivityTotals {
  totalSchedules: number;
  cancelledByPatient: number;
  cancelledByProfessional: number;
  attended: number;
  evolved: number;
  payoutAttended: number;
  payoutEvolved: number;
  evolvedRate: number;
  attendanceRate: number;
}

export interface ProfessionalProductivityResponse {
  totals: ProfessionalProductivityTotals;
  items: ProfessionalProductivityItem[];
  byService: ProductivityServiceRow[];
  monthly: ProfessionalProductivityMonth[];
}

/** Uma sessão realizada, para a impressão por profissional (RN-215e). */
export interface ProductivityDetailItem {
  professionalExternalId: string;
  professionalName: string;
  /** AAAA-MM-DD, já no relógio da entidade */
  date: string;
  /** HH:mm */
  startTime: string;
  patientName: string;
  serviceName: string;
  evolved: boolean;
}

export const reportsService = {
  getScheduleAnalysis: async (
    params: ScheduleAnalysisParams
  ): Promise<ScheduleAnalysisResponse> => {
    const response = await api.get("/reports/schedule-analysis", {
      params: {
        from: params.from,
        to: params.to,
        professionals: params.professionalIds?.length
          ? params.professionalIds.join(",")
          : undefined,
      },
    });
    return response.data;
  },

  getProfessionals: async (): Promise<ReportProfessionalItem[]> => {
    const response = await api.get("/reports/schedule-analysis/professionals");
    return response.data;
  },

  // planilha .xlsx gerada no backend (ClosedXML); onlyCancelled = só cancelamentos + Justificativa
  /** cancelamentos de um paciente no período (modal do relatório de Cancelamentos) */
  getPatientCancellations: async (params: ScheduleAnalysisParams, patientId: string): Promise<ScheduleDetailItem[]> => {
    const response = await api.get("/reports/schedule-analysis/details", {
      params: {
        from: params.from,
        to: params.to,
        professionals: params.professionalIds?.length ? params.professionalIds.join(",") : undefined,
        onlyCancelled: true,
        patientId,
      },
    });
    return response.data;
  },

  exportXlsx: async (params: ScheduleAnalysisParams, onlyCancelled = false): Promise<Blob> => {
    const response = await api.get("/reports/schedule-analysis/export", {
      params: {
        from: params.from,
        to: params.to,
        professionals: params.professionalIds?.length
          ? params.professionalIds.join(",")
          : undefined,
        onlyCancelled: onlyCancelled || undefined,
      },
      responseType: "blob",
    });
    return response.data;
  },

  /** RN-215: mesmos filtros da Análise de Atendimentos, para os dois serem comparados no mesmo recorte. */
  getProfessionalProductivity: async (
    params: ScheduleAnalysisParams
  ): Promise<ProfessionalProductivityResponse> => {
    const response = await api.get("/reports/professional-productivity", {
      params: {
        from: params.from,
        to: params.to,
        professionals: params.professionalIds?.length
          ? params.professionalIds.join(",")
          : undefined,
      },
    });
    return response.data;
  },

  /** RN-215e: sessões realizadas, uma por linha — mesmo recorte do relatório. */
  getProductivityDetails: async (
    params: ScheduleAnalysisParams
  ): Promise<ProductivityDetailItem[]> => {
    const response = await api.get("/reports/professional-productivity/details", {
      params: {
        from: params.from,
        to: params.to,
        professionals: params.professionalIds?.length
          ? params.professionalIds.join(",")
          : undefined,
      },
    });
    return response.data;
  },
};


// ===== Análise de Cancelamentos =====

export interface CancellationTotals {
  totalSchedules: number;
  totalCancelled: number;
  cancellationRate: number;
  cancelledByPatient: number;
  cancelledByProfessional: number;
  cancelledOther: number;
  grossImpact: number;
  rescheduledCount: number;
  rescheduledRate: number;
  valueSavedByRescheduling: number;
}

export interface CancellationTypeItem {
  type: string;
  count: number;
  color?: string | null;
  grossValue: number;
}

export interface CancellingPatientItem {
  patientExternalId: string;
  patientName: string;
  cancellations: number;
  withJustification: number;
  /** soma do valor previsto dos agendamentos cancelados */
  grossImpact: number;
}

export interface CancellationOriginItem {
  origin: string;
  totalSchedules: number;
  totalCancelled: number;
  cancellationRate: number;
  grossImpact: number;
}

export interface CancellationPlanItem {
  plan: string;
  origin: string;
  totalSchedules: number;
  cancelledByPatient: number;
  cancelledByProfessional: number;
  cancelledOther: number;
  totalCancelled: number;
  cancellationRate: number;
  rescheduledCount: number;
  grossImpact: number;
}

/** Linha analítica do período (mesma do export); aqui usada no modal de cancelamentos do paciente. */
export interface ScheduleDetailItem {
  scheduleExternalId: string;
  date: string;
  startTime: string;
  endTime: string;
  patientName: string | null;
  professionalName: string | null;
  confirmationStatus: string | null;
  presenceStatus: string | null;
  justification: string | null;
  serviceName?: string | null;
  planName?: string | null;
  plannedValue?: number | null;
}

export interface CancellingProfessionalItem {
  professionalExternalId: string;
  professionalName: string;
  cancellations: number;
  withJustification: number;
  lastJustification?: string | null;
}

export interface ProfessionalAgendaImpactItem {
  professionalExternalId: string;
  professionalName: string;
  totalSchedules: number;
  cancelledByPatient: number;
  cancelledByHoliday: number;
  cancelledByProfessional: number;
  cancelledOther: number;
  totalCancelled: number;
  cancellationRate: number;
  grossImpact: number;
}

export interface MonthlyCancellationItem {
  month: string;
  totalSchedules: number;
  totalCancelled: number;
  cancellationRate: number;
}

export interface CancellationAnalysisResponse {
  totals: CancellationTotals;
  byType: CancellationTypeItem[];
  topPatients: CancellingPatientItem[];
  professionalCancellations: CancellingProfessionalItem[];
  agendaImpact: ProfessionalAgendaImpactItem[];
  monthly: MonthlyCancellationItem[];
  /** cancelamentos por origem do serviço (Particular × Convênio) */
  byOrigin: CancellationOriginItem[];
  /** uma linha por plano com cancelamentos por tipo, taxa, reagendados e impacto */
  byPlan: CancellationPlanItem[];
}

export const cancellationReportService = {
  get: async (params: ScheduleAnalysisParams): Promise<CancellationAnalysisResponse> => {
    const response = await api.get("/reports/cancellation-analysis", {
      params: {
        from: params.from,
        to: params.to,
        professionals: params.professionalIds?.length
          ? params.professionalIds.join(",")
          : undefined,
      },
    });
    return response.data;
  },
};

// ===== Evolutivo de Atendimentos =====

export interface EvolutionMonthItem {
  month: string;
  scheduled: number;
  attended: number;
  cancelledByPatient: number;
  cancelledByProfessional: number;
  netValue: number;
}

export interface ProfessionalEvolutionItem {
  professionalExternalId: string;
  professionalName: string;
  totalAttended: number;
  series: EvolutionMonthItem[];
}

export interface EvolutionResponse {
  monthly: EvolutionMonthItem[];
  topProfessionals: ProfessionalEvolutionItem[];
}

export interface ProjectionPoint {
  month: string;
  value: number;
}

export interface ProjectionForecastPoint {
  month: string;
  value: number;
  low: number;
  high: number;
}

export interface ProjectionSeries {
  history: ProjectionPoint[];
  forecast: ProjectionForecastPoint[];
  model: string;
  mape?: number | null;
}

export interface ProjectionResponse {
  attended: ProjectionSeries;
  revenue: ProjectionSeries;
  warnings: string[];
}

export const evolutionReportService = {
  get: async (fromMonth: string, toMonth: string): Promise<EvolutionResponse> => {
    const response = await api.get("/reports/evolution", {
      params: { fromMonth, toMonth },
    });
    return response.data;
  },

  // planilha .xlsx: série mensal da clínica + série por profissional (padrão dos relatórios)
  exportXlsx: async (fromMonth: string, toMonth: string): Promise<Blob> => {
    const response = await api.get("/reports/evolution/export", {
      params: { fromMonth, toMonth },
      responseType: "blob",
    });
    return response.data;
  },

  getProjection: async (horizon = 6): Promise<ProjectionResponse> => {
    const response = await api.get("/reports/evolution/projection", {
      params: { horizon },
    });
    return response.data;
  },
};

// ===== Novos Pacientes e Evasão (30 dias) =====

export interface PatientFlowSummary {
  newCount: number;
  churnCount: number;
  returnCount: number;
  netBalance: number;
  attendedPatients: number;
}

export interface PatientFlowMonthItem {
  month: string;
  newCount: number;
  /** bebês (<1 ano na data do 1º atendimento) — já incluídos em newCount */
  newBabiesCount: number;
  churnCount: number;
  /** bebês (<1 ano na data em que a evasão se completou) — já incluídos em churnCount */
  churnBabiesCount: number;
  returnCount: number;
}

export interface NewPatientItem {
  patientExternalId: string;
  patientName: string;
  /** anos ("14") ou meses para bebês ("7m") */
  age?: string | null;
  /** dd/MM/yyyy (formatado no backend — data de agenda não desloca fuso) */
  firstVisitDate: string;
  professionalName: string;
  /** serviço do atendimento + especialidade, ex.: "Sessão de atendimento (Fonoaudiologia)" */
  serviceType: string;
  visitsInMonth: number;
  registerOrigin?: string | null;
}

export interface ChurnPatientItem {
  patientExternalId: string;
  patientName: string;
  /** anos ("14") ou meses para bebês ("7m") */
  age?: string | null;
  /** dd/MM/yyyy */
  lastVisitDate: string;
  daysWithoutVisit: number;
  professionalName: string;
  serviceType: string;
  returnedDate?: string | null;
  registerRemoved: boolean;
}

export interface ReturnPatientItem {
  patientExternalId: string;
  patientName: string;
  /** anos ("14") ou meses para bebês ("7m") */
  age?: string | null;
  /** dd/MM/yyyy */
  returnedDate: string;
  daysAway: number;
  /** dd/MM/yyyy — último atendimento antes da pausa */
  previousVisitDate: string;
  professionalName: string;
  serviceType: string;
}

export interface PatientFlowResponse {
  month: string;
  /** competência corrente: evasões ainda em aberto não aparecem */
  isPartialMonth: boolean;
  summary: PatientFlowSummary;
  monthly: PatientFlowMonthItem[];
  newPatients: NewPatientItem[];
  churnPatients: ChurnPatientItem[];
  /** exibidos apenas no export xlsx (aba Retornos no mês) */
  returnPatients: ReturnPatientItem[];
}

export const patientFlowReportService = {
  get: async (month: string): Promise<PatientFlowResponse> => {
    const response = await api.get("/reports/patient-flow", { params: { month } });
    return response.data;
  },

  // planilha .xlsx: evolutivo 12 meses + listas de novos e evasão do mês
  exportXlsx: async (month: string): Promise<Blob> => {
    const response = await api.get("/reports/patient-flow/export", {
      params: { month },
      responseType: "blob",
    });
    return response.data;
  },
};

// ===== Demonstração do Resultado (DRE) =====

export interface DreCategoryItem {
  name: string;
  current: number;
  previous: number;
}

export interface DreLine {
  key: string;
  label: string;
  isResult: boolean;
  current: number;
  previous: number;
  variationPercent?: number | null;
  percentOfRevenue?: number | null;
  categories: DreCategoryItem[];
}

export interface DreConfigVersion {
  id: string;
  version: number;
  createdAt: string;
}

export interface DreResponse {
  month: string;
  previousMonth: string;
  lines: DreLine[];
  warnings: string[];
  // RN-210c: versões ativas da configuração DRE x plano de contas; vazio = legado (campo dre do Bubble)
  configVersions: DreConfigVersion[];
  appliedConfigId?: string | null;
}

export const dreReportService = {
  get: async (month: string, configurationId?: string): Promise<DreResponse> => {
    const response = await api.get("/reports/dre", {
      params: { month, configurationId: configurationId || undefined },
    });
    return response.data;
  },

  // planilha .xlsx com o mesmo conteúdo da tela (padrão de todos os relatórios)
  exportXlsx: async (month: string, configurationId?: string): Promise<Blob> => {
    const response = await api.get("/reports/dre/export", {
      params: { month, configurationId: configurationId || undefined },
      responseType: "blob",
    });
    return response.data;
  },

  // planilha analítica (RN-210d): DRE com fórmulas + lançamentos + agenda + fora da DRE
  exportAnalyticalXlsx: async (month: string, configurationId?: string): Promise<Blob> => {
    const response = await api.get("/reports/dre/analytical-export", {
      params: { month, configurationId: configurationId || undefined },
      responseType: "blob",
    });
    return response.data;
  },
};

// ===== Fluxo de Caixa =====

export interface CashFlowTotals {
  totalInflow: number;
  totalOutflow: number;
  netCash: number;
}

export interface CashFlowTypeItem {
  name: string;
  value: number;
}

export interface CashFlowMonthItem {
  month: string;
  inflow: number;
  outflow: number;
}

export interface CashFlowBalanceItem {
  name: string;
  balance: number;
  balanceDate: string;
}

export interface CashFlowReconciliation {
  startingBalance: number;
  startingAccounts: CashFlowBalanceItem[];
  reserveBalance: number;
  reserveAccounts: CashFlowBalanceItem[];
  internalMovements?: number;
  endingBalanceEstimated: number;
  endingBalanceRecorded?: number | null;
  difference?: number | null;
  excludedItems: CashFlowTypeItem[];
}

export interface CashFlowCurrentBalances {
  accounts: CashFlowBalanceItem[];
  total: number;
}

export interface CashFlowCategoryOption {
  name: string;
  subCategories: string[];
}

export interface CashFlowFilterOptions {
  categories: CashFlowCategoryOption[];
  accounts: string[];
}

export interface CashFlowResponse {
  totals: CashFlowTotals;
  inflows: CashFlowTypeItem[];
  outflows: CashFlowTypeItem[];
  topExpenses: CashFlowTypeItem[];
  monthly: CashFlowMonthItem[];
  reconciliation: CashFlowReconciliation;
  currentBalances?: CashFlowCurrentBalances | null;
  filterOptions?: CashFlowFilterOptions | null;
  warnings: string[];
}

/** Filtros da tela: todos opcionais e combináveis (subcategoria é filha da categoria). */
export interface CashFlowFilters {
  search?: string;
  category?: string;
  subCategory?: string;
  operation?: string;
  account?: string;
}

export interface CashFlowDetailItem {
  date: string;
  /** nome do lançamento (campo `account` do Bubble: "Aluguel matriz") */
  description: string;
  /** observação do lançamento (campo `description` do Bubble; raro) */
  notes: string;
  group: string;
  category: string;
  subCategory: string;
  operationType: string;
  value: number;
  /** conta bancária */
  account: string;
  inCashFlow: boolean;
}

export interface CashFlowDetailsPage {
  items: CashFlowDetailItem[];
  total: number;
}

const cashFlowParams = (from: string, to: string, filters?: CashFlowFilters) => ({
  from,
  to,
  ...(filters?.search ? { search: filters.search } : {}),
  ...(filters?.category ? { category: filters.category } : {}),
  ...(filters?.subCategory ? { subCategory: filters.subCategory } : {}),
  ...(filters?.operation ? { operation: filters.operation } : {}),
  ...(filters?.account ? { account: filters.account } : {}),
});

export const cashFlowReportService = {
  get: async (from: string, to: string, filters?: CashFlowFilters): Promise<CashFlowResponse> => {
    const response = await api.get("/reports/cashflow", {
      params: cashFlowParams(from, to, filters),
    });
    return response.data;
  },

  // lista paginada de lançamentos do período (respeita os filtros da tela)
  details: async (
    from: string,
    to: string,
    page: number,
    pageSize: number,
    filters?: CashFlowFilters,
  ): Promise<CashFlowDetailsPage> => {
    const response = await api.get("/reports/cashflow/details", {
      params: { ...cashFlowParams(from, to, filters), page, pageSize },
    });
    return response.data;
  },

  // planilha .xlsx com os lançamentos do período (ClosedXML no backend; mesmos filtros)
  exportXlsx: async (from: string, to: string, filters?: CashFlowFilters): Promise<Blob> => {
    const response = await api.get("/reports/cashflow/export", {
      params: cashFlowParams(from, to, filters),
      responseType: "blob",
    });
    return response.data;
  },
};

// ===== Pacientes × Atendimentos × Profissionais =====

export interface PatientScheduleItem {
  patientExternalId: string;
  patientName: string;
  professionalExternalId: string;
  professionalName: string;
  specialty: string;
  scheduled: number;
  cancelledByPatient: number;
  cancelledByProfessional: number;
  noStatus: number;
  attended: number;
  unitValue: number;
  paidCount: number;
  paidValue: number;
  pendingCount: number;
  pendingValue: number;
  totalValue: number;
}

export interface ReportPatientItem {
  externalId: string;
  name: string;
}

export interface PatientScheduleDetailItem {
  patientName: string;
  date: string;
  startTime: string;
  endTime: string;
  professionalName: string;
  specialty: string;
  paymentStatus: string;
  normalValue: number;
  discount: number;
  finalValue: number;
}

export const patientSchedulesReportService = {
  get: async (params: {
    from: string;
    to: string;
    professionalIds?: string[];
    patientIds?: string[];
  }): Promise<PatientScheduleItem[]> => {
    const response = await api.get("/reports/patient-schedules", {
      params: {
        from: params.from,
        to: params.to,
        professionals: params.professionalIds?.length ? params.professionalIds.join(",") : undefined,
        patients: params.patientIds?.length ? params.patientIds.join(",") : undefined,
      },
    });
    return response.data;
  },

  getPatients: async (): Promise<ReportPatientItem[]> => {
    const response = await api.get("/reports/patient-schedules/patients");
    return response.data;
  },

  getDetails: async (params: {
    from: string;
    to: string;
    professionalIds?: string[];
    patientIds?: string[];
  }): Promise<PatientScheduleDetailItem[]> => {
    const response = await api.get("/reports/patient-schedules/details", {
      params: {
        from: params.from,
        to: params.to,
        professionals: params.professionalIds?.length ? params.professionalIds.join(",") : undefined,
        patients: params.patientIds?.length ? params.patientIds.join(",") : undefined,
      },
    });
    return response.data;
  },

  // .xlsx com duas abas: Consolidado (como na tela) + Atendimentos (analítica por agendamento)
  exportXlsx: async (params: {
    from: string;
    to: string;
    professionalIds?: string[];
    patientIds?: string[];
  }): Promise<Blob> => {
    const response = await api.get("/reports/patient-schedules/export", {
      params: {
        from: params.from,
        to: params.to,
        professionals: params.professionalIds?.length ? params.professionalIds.join(",") : undefined,
        patients: params.patientIds?.length ? params.patientIds.join(",") : undefined,
      },
      responseType: "blob",
    });
    return response.data;
  },
};

// ===== Ocupação de Salas =====

export interface OccupancySummary {
  roomsTotal: number;
  /** salas que entram no cálculo de capacidade (a capacidade já desconta os dias alugados) */
  roomsInPool: number;
  /** horas agendadas com sala conhecida */
  hoursBooked: number;
  /** horas de atendimento de profissional sem sala padrão — aconteceram em alguma sala, mas não se sabe qual */
  hoursWithoutRoom: number;
  /** capacidade do período já descontando os dias em que cada sala está alugada */
  hoursCapacity: number;
  /** horas fora do pool (salas alugadas a terceiro) — receita fixa, não ociosidade */
  hoursRented: number;
  /** piso da banda: hoursBooked / hoursCapacity */
  occupancyAssignedPercent: number;
  /** teto da banda: (hoursBooked + hoursWithoutRoom) / hoursCapacity */
  occupancyRealPercent: number;
  sessionsWithoutRoom: number;
  professionalsWithoutRoom: number;
  /** atendimentos sobrepostos na mesma sala — impossível na vida real, indica sala sugerida errada */
  overlappingSessions: number;
  from: string;
  to: string;
  /** faixa de horário útil considerada na capacidade (ex.: "08:00–19:00") */
  businessHours: string;
}

export interface OccupancyRoomItem {
  roomId: string;
  roomName: string;
  /** dias alugados já traduzidos pelo backend (ex.: ["Segunda", "Quarta"]) */
  rentalWeekdays: string[];
  fullyRented: boolean;
  specialties: string[];
  sessions: number;
  hoursBooked: number;
  hoursCapacity: number;
  occupancyPercent: number;
  overlappingSessions: number;
  professionals: string[];
}

export interface OccupancyShiftItem {
  shift: string;
  hoursBooked: number;
  hoursCapacity: number;
  occupancyPercent: number;
}

export interface OccupancyWeekdayItem {
  weekday: string;
  hoursBooked: number;
  hoursCapacity: number;
  occupancyPercent: number;
}

// Célula do mapa de calor: ocupação % de uma faixa dia da semana × hora (weekday 1=seg … 6=sáb).
export interface OccupancyHeatCell {
  weekday: number;
  hour: number;
  occupancyPercent: number;
  hoursBooked: number;
  hoursCapacity: number;
  sessions: number;
}

// Linha do tempo de horas agendadas (volume). granularity: "diaria" | "semanal".
export interface OccupancyTrend {
  granularity: string;
  points: { label: string; hoursBooked: number }[];
}

export interface OccupancyResponse {
  summary: OccupancySummary;
  rooms: OccupancyRoomItem[];
  shifts: OccupancyShiftItem[];
  weekdays: OccupancyWeekdayItem[];
  heatmap: OccupancyHeatCell[];
  trend: OccupancyTrend;
}

export const occupancyReportService = {
  get: async (from: string, to: string): Promise<OccupancyResponse> => {
    const response = await api.get("/reports/occupancy", { params: { from, to } });
    return response.data;
  },
};

// ===== Cenário de Caixa (projeção preventiva) =====

export interface ScenarioAccountItem {
  name: string;
  balance: number;
  balanceDate: string;
}

export interface ScenarioDetailItem {
  name: string;
  value: number;
  source: string;
}

export interface ScenarioMonthItem {
  month: string;
  isProjected: boolean;
  expectedInflow: number;
  inflowLow: number;
  inflowHigh: number;
  fixedExpenses: number;
  payouts: number;
  taxes: number;
  netResult: number;
  endingBalance?: number | null;
  fixedItems: ScenarioDetailItem[];
}

export interface CashScenarioResponse {
  startingBalance: number;
  startingBalanceNote: string;
  accounts: ScenarioAccountItem[];
  reserveBalance: number;
  reserveAccounts: ScenarioAccountItem[];
  months: ScenarioMonthItem[];
  warnings: string[];
}

/** Os mesmos totais da Análise de Atendimentos, no período do painel. */
export interface CashBookAttendances {
  totalSchedules: number;
  attended: number;
  chargeableNoShows: number;
  cancelled: number;
  attendanceRate: number;
  realizedValue: number;
  paidValue: number;
  advancePaidValue: number;
  pendingValue: number;
  pendingCount: number;
  professionalPayout: number;
  taxRegime: string | null;
  taxPercent: number;
  taxForecast: number;
}

/** Painel do Cenário de caixa: provisões em aberto, saldo em contas e atendimentos do mês. */
export interface CashScenarioPanel {
  forecastPayable: CashBookSlice[];
  forecastReceivable: CashBookSlice[];
  byAccount: CashBookSlice[];
  attendances?: CashBookAttendances | null;
}

export const cashScenarioReportService = {
  get: async (fromMonth: string, toMonth: string): Promise<CashScenarioResponse> => {
    const response = await api.get("/reports/cash-scenario", { params: { fromMonth, toMonth } });
    return response.data;
  },

  /** Painel do mês inicial (from/to em yyyy-MM-dd, ambos inclusivos). */
  panel: async (from: string, to: string): Promise<CashScenarioPanel> => {
    const response = await api.get("/reports/cash-scenario/panel", { params: { from, to } });
    return response.data;
  },

  // planilha .xlsx: posição das contas, projeção mês a mês e composição dos gastos fixos
  exportXlsx: async (fromMonth: string, toMonth: string): Promise<Blob> => {
    const response = await api.get("/reports/cash-scenario/export", {
      params: { fromMonth, toMonth },
      responseType: "blob",
    });
    return response.data;
  },
};

/**
 * Exclusões de relatórios (RN-207): agendamentos que não entram nos relatórios da clínica.
 * Três formas — só paciente (global), só profissional (todos os agendamentos dele) ou o PAR
 * (aquele profissional com aquele paciente). A chave é o id EXTERNO, que é o que a tabela guarda.
 */
export interface PrivatePatientExclusion {
  id: string;
  professionalExternalId: string | null;
  professionalName: string | null;
  patientExternalId: string | null;
  patientName: string | null;
  /** global | professional | pair */
  scope: string;
  reason: string | null;
  active: boolean;
  createdAt: string;
}

export interface ExclusionOption {
  externalId: string;
  name: string;
}

export const privatePatientExclusionsService = {
  list: async (includeInactive: boolean): Promise<PrivatePatientExclusion[]> => {
    const response = await api.get("/private-patient-exclusions", { params: { includeInactive } });
    return response.data;
  },

  professionals: async (): Promise<ExclusionOption[]> => {
    const response = await api.get("/private-patient-exclusions/professionals");
    return response.data;
  },

  patients: async (): Promise<ExclusionOption[]> => {
    const response = await api.get("/private-patient-exclusions/patients");
    return response.data;
  },

  create: async (data: {
    professionalExternalId?: string | null;
    patientExternalId?: string | null;
    reason?: string | null;
  }): Promise<string> => {
    const response = await api.post("/private-patient-exclusions", data);
    return response.data;
  },

  update: async (id: string, data: { reason?: string | null; active?: boolean }): Promise<void> => {
    await api.put(`/private-patient-exclusions/${id}`, data);
  },

  remove: async (id: string): Promise<void> => {
    await api.delete(`/private-patient-exclusions/${id}`);
  },
};
